// pages/setup/pageOverrides.ts
//
// Regras por página: lógica pura (sem React), para poder testar.
// As regras de validação espelham `PageOverride` em
// api-rocketads/models/ad_setup_schemas.py — se mudar lá, mude aqui.
import type {
    PageBlockOverride,
    PageOverride,
    PageOverrideBlockKey,
} from '@/types/ad-setup';

export const MAX_PAGE_OVERRIDES = 50;
export const MAX_PATH_LENGTH = 500;
export const MAX_LABEL_LENGTH = 120;
export const MAX_REFERENCE_LENGTH = 100;
export const MIN_INSIDE_CONTAINER = 1;
export const MAX_INSIDE_CONTAINER = 200;

export type PageBlockKind = 'positioned' | 'sticky' | 'plain';

export interface PageOverrideBlockDef {
    key: PageOverrideBlockKey;
    label: string;
    /** Chave do bloco no estado do Setup (`setupData`), para mostrar o valor do site. */
    siteKey: 'Topo' | 'Content1' | 'Content2' | 'anchor' | 'fixed' | 'interstitial' | 'trigger';
    kind: PageBlockKind;
}

export const PAGE_OVERRIDE_BLOCKS: PageOverrideBlockDef[] = [
    { key: 'topo', label: 'Topo', siteKey: 'Topo', kind: 'positioned' },
    { key: 'content1', label: 'Content 1', siteKey: 'Content1', kind: 'positioned' },
    { key: 'content2', label: 'Content 2', siteKey: 'Content2', kind: 'positioned' },
    { key: 'anchor', label: 'Anchor', siteKey: 'anchor', kind: 'sticky' },
    { key: 'fixed', label: 'Fixed', siteKey: 'fixed', kind: 'sticky' },
    { key: 'interstitial', label: 'Interstitial', siteKey: 'interstitial', kind: 'plain' },
    { key: 'trigger', label: 'Trigger', siteKey: 'trigger', kind: 'plain' },
];

const BLOCK_DEF = Object.fromEntries(
    PAGE_OVERRIDE_BLOCKS.map((b) => [b.key, b])
) as Record<PageOverrideBlockKey, PageOverrideBlockDef>;

/** Regra como a tela a edita: igual à da API, mais um id local para a lista. */
export interface PageOverrideDraft {
    uid: string;
    path: string;
    label: string;
    blocks: Partial<Record<PageOverrideBlockKey, PageBlockOverride>>;
}

export type BlockMode = 'inherit' | 'on' | 'off';

let uidCounter = 0;
export function newUid(): string {
    uidCounter += 1;
    return `pg-${Date.now().toString(36)}-${uidCounter}`;
}

/**
 * Mesma normalização da API: aceita a URL inteira colada da barra do navegador
 * e guarda só o path (o script compara com `location.pathname`).
 * Equivale ao `urlsplit(value).path or "/"` do Python — sem decodificar nem
 * codificar nada, para o front e a API chegarem no mesmo texto.
 */
export function normalizePagePath(raw: string): string {
    const value = raw.trim();
    const match = /^https?:\/\/[^/?#]*/i.exec(value);
    if (!match) return value;
    const rest = value.slice(match[0].length);
    const path = rest.split(/[?#]/, 1)[0];
    return path || '/';
}

/** Chave de unicidade: a API ignora a barra final ao comparar paths. */
function pathKey(path: string): string {
    return path.replace(/\/+$/, '') || '/';
}

export function blockMode(block: PageBlockOverride | undefined): BlockMode {
    if (!block) return 'inherit';
    if (block.enabled === false) return 'off';
    // `enabled` ausente com campo de posição é "mantém ligado/desligado do site,
    // mas ajusta a posição" — a tela trata como "Ligar" sem forçar o enabled.
    return 'on';
}

/**
 * Limpa um bloco para o formato que a API aceita: só os campos que aquele tipo
 * de bloco conhece, sem null/undefined/string vazia/NaN. Devolve `null` quando
 * não sobra nada — bloco vazio `{}` não é enviado (bloco "herdar" não aparece).
 */
export function cleanBlock(
    key: PageOverrideBlockKey,
    block: PageBlockOverride | null | undefined
): PageBlockOverride | null {
    if (!block || typeof block !== 'object') return null;
    const kind = BLOCK_DEF[key].kind;
    const out: PageBlockOverride = {};

    if (typeof block.enabled === 'boolean') out.enabled = block.enabled;

    // Desligado: posição não faz diferença, não manda.
    const off = block.enabled === false;

    if (kind === 'positioned' && !off) {
        const reference = typeof block.reference === 'string' ? block.reference.trim() : '';
        if (reference) out.reference = reference;
        if (typeof block.inside_container === 'number' && Number.isFinite(block.inside_container)) {
            out.inside_container = block.inside_container;
        }
        if (block.position_reference === 'before' || block.position_reference === 'after') {
            out.position_reference = block.position_reference;
        }
        if (typeof block.lazy_load === 'boolean') out.lazy_load = block.lazy_load;
    }

    if (kind === 'sticky' && !off) {
        if (block.fixed_position === 'top' || block.fixed_position === 'bottom') {
            out.fixed_position = block.fixed_position;
        }
    }

    return Object.keys(out).length > 0 ? out : null;
}

function cleanBlocks(
    blocks: Partial<Record<PageOverrideBlockKey, PageBlockOverride | null>> | null | undefined
): Partial<Record<PageOverrideBlockKey, PageBlockOverride>> {
    const out: Partial<Record<PageOverrideBlockKey, PageBlockOverride>> = {};
    if (!blocks || typeof blocks !== 'object') return out;
    for (const def of PAGE_OVERRIDE_BLOCKS) {
        const cleaned = cleanBlock(def.key, blocks[def.key]);
        if (cleaned) out[def.key] = cleaned;
    }
    return out;
}

/** GET → estado da tela. Tolerante: campo ausente/null vira "herdar". */
export function draftsFromApi(list: unknown): PageOverrideDraft[] {
    if (!Array.isArray(list)) return [];
    return list
        .filter((item): item is PageOverride => !!item && typeof item === 'object')
        .map((item) => ({
            uid: newUid(),
            path: typeof item.path === 'string' ? item.path : '',
            label: typeof item.label === 'string' ? item.label : '',
            blocks: cleanBlocks(item.blocks),
        }));
}

/** Estado da tela → `page_overrides` do PUT/POST. */
export function draftsToPayload(drafts: PageOverrideDraft[]): PageOverride[] {
    return drafts.map((draft) => {
        const rule: PageOverride = {
            path: normalizePagePath(draft.path),
            blocks: cleanBlocks(draft.blocks),
        };
        const label = draft.label.trim();
        if (label) rule.label = label;
        return rule;
    });
}

export interface PageOverrideError {
    /** Regra com problema; `null` para erro da lista inteira. */
    uid: string | null;
    message: string;
}

export function validatePath(rawPath: string): string | null {
    const path = normalizePagePath(rawPath);
    if (!path) return 'Informe o path da página.';
    if (!path.startsWith('/')) return 'O path deve começar com / (ex.: /meu-artigo/).';
    if (path.length > MAX_PATH_LENGTH) return `O path passa de ${MAX_PATH_LENGTH} caracteres.`;
    if (/\s/.test(path) || path.includes('?') || path.includes('#')) {
        return 'O path não pode ter espaço, ? nem #.';
    }
    if (path.slice(0, -1).includes('*')) {
        return 'O * só é aceito no fim do path (ex.: /rec-*).';
    }
    return null;
}

/** Espelha a validação da API, com mensagem em português por regra. */
export function validatePageOverrides(drafts: PageOverrideDraft[]): PageOverrideError[] {
    const errors: PageOverrideError[] = [];

    if (drafts.length > MAX_PAGE_OVERRIDES) {
        errors.push({
            uid: null,
            message: `No máximo ${MAX_PAGE_OVERRIDES} regras por domínio. Considere um padrão com * no fim.`,
        });
    }

    const seen = new Map<string, number>();
    drafts.forEach((draft, index) => {
        const push = (message: string) => errors.push({ uid: draft.uid, message });

        const pathError = validatePath(draft.path);
        if (pathError) {
            push(pathError);
        } else {
            const key = pathKey(normalizePagePath(draft.path));
            const first = seen.get(key);
            if (first !== undefined) {
                push(`Path repetido: já usado na regra ${first + 1}.`);
            } else {
                seen.set(key, index);
            }
        }

        if (draft.label.trim().length > MAX_LABEL_LENGTH) {
            push(`O nome passa de ${MAX_LABEL_LENGTH} caracteres.`);
        }

        const blocks = cleanBlocks(draft.blocks);
        if (Object.keys(blocks).length === 0) {
            push('Escolha ao menos um bloco para ligar ou desligar nesta página.');
        }

        for (const def of PAGE_OVERRIDE_BLOCKS) {
            const block = blocks[def.key];
            if (!block) continue;
            if (block.reference !== undefined && block.reference.length > MAX_REFERENCE_LENGTH) {
                push(`${def.label}: a referência HTML passa de ${MAX_REFERENCE_LENGTH} caracteres.`);
            }
            if (
                block.inside_container !== undefined &&
                (!Number.isInteger(block.inside_container) ||
                    block.inside_container < MIN_INSIDE_CONTAINER ||
                    block.inside_container > MAX_INSIDE_CONTAINER)
            ) {
                push(
                    `${def.label}: a posição do container deve ser um número entre ${MIN_INSIDE_CONTAINER} e ${MAX_INSIDE_CONTAINER}.`
                );
            }
        }
    });

    return errors;
}

/** Resumo para a lista: "Content 1 on · Content 2 off". */
export function summarizeDraft(draft: PageOverrideDraft): string {
    const parts: string[] = [];
    for (const def of PAGE_OVERRIDE_BLOCKS) {
        const block = draft.blocks[def.key];
        const mode = blockMode(block);
        if (mode === 'inherit') continue;
        if (mode === 'off') parts.push(`${def.label} off`);
        else parts.push(block?.enabled === true ? `${def.label} on` : `${def.label} posição`);
    }
    return parts.length > 0 ? parts.join(' · ') : 'Nenhum bloco alterado';
}

/**
 * O `detail` da API vem como string (400) ou como a lista de erros do pydantic
 * (422). A lista não pode ir direto para o toast: React não renderiza objeto.
 */
export function formatApiErrorDetail(detail: unknown, fallback = 'Ocorreu um erro.'): string {
    if (typeof detail === 'string' && detail) return detail;
    if (Array.isArray(detail)) {
        const messages = detail
            .map((item) => {
                if (!item || typeof item !== 'object') return String(item);
                const { msg, loc } = item as { msg?: unknown; loc?: unknown };
                const text = typeof msg === 'string' ? msg.replace(/^Value error, /, '') : '';
                const where = Array.isArray(loc) ? loc.filter((p) => p !== 'body').join('.') : '';
                return where ? `${where}: ${text}` : text;
            })
            .filter(Boolean);
        if (messages.length > 0) return messages.join(' | ');
    }
    return fallback;
}
